import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert } from 'subframe/components/Alert';
import { Button } from 'subframe/components/Button';
import endpoints from '../../constants/endpoints';
import { getCandidatesRoute } from '../../constants/routes';
import { getCandidateDetail } from '../../services/recruit';
import { IconName } from '../../types';
import { Breadcrumb } from './components/Breadcrumb';
import { CandidateHeader } from './components/CandidateHeader';
import { CandidateTimeline } from './components/CandidateTimeline';

import { ApiResponse, CandidateDetailData } from 'modules/Recruit/types/api';

function CandidateDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const candidateId = parseInt(id || '');

  const {
    data: candidate,
    isLoading: isLoadingCandidate,
    error: candidateError,
    isError: isCandidateError,
  } = useQuery({
    queryKey: [endpoints.candidateDetail, candidateId],
    queryFn: () => getCandidateDetail(candidateId),
    enabled: !!candidateId,
    select: (res: ApiResponse<CandidateDetailData>) => res?.result?.candidate_detail,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
  });

  const handleBackToList = () => {
    navigate(getCandidatesRoute());
  };

  const breadcrumbItems = [
    {
      icon: IconName.FEATHER_USERS,
      label: 'Candidates',
      href: getCandidatesRoute(),
    },
    {
      icon: IconName.FEATHER_USER,
      label: candidate?.first_name
        ? `${candidate.first_name} ${candidate.last_name}`
        : 'Candidate Profile',
      href: '#',
    },
  ];

  if (isCandidateError) {
    return (
      <div className="flex h-full w-full flex-col items-start gap-2">
        <Breadcrumb items={breadcrumbItems} />
        <div className="flex w-full max-w-[1280px] grow shrink-0 basis-0 flex-col items-start gap-4 md:gap-8 bg-default-background px-4 md:px-12 pt-6 pb-12">
          <Alert variant="error" title="Error loading candidate">
            {candidateError?.message || 'Failed to load candidate data. Please try again.'}
          </Alert>
          <Button onClick={handleBackToList} variant="neutral-secondary">
            Back to Candidates
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full w-full flex-col items-start gap-4 bg-default-background">
      <div className="flex w-full grow shrink-0 basis-0 flex-col items-start">
        <Breadcrumb items={breadcrumbItems} />
        <div className="flex w-full shrink-0 basis-0 flex-col items-start">
          <div className="flex w-full max-w-[1120px] flex-col items-start gap-2 px-12 pb-3">
            <CandidateHeader candidateId={candidateId} />
          </div>
        </div>
        <div className="flex w-full grow shrink-0 basis-0 flex-col items-start gap-6 bg-neutral-50 px-12 pt-4 pb-12">
          <div className="flex w-256 grow shrink-0 basis-0 flex-col items-start gap-4 rounded-lg bg-white px-12 py-12">
            <CandidateTimeline candidateId={candidateId} />
          </div>
        </div>
      </div>
    </div>
  );
}

export default CandidateDetailPage;
